import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { ConfigContext } from '@chr/navisphereonline-shared-components';
import { CircularProgress } from '@chr/eds-react';

import { useGetBooking } from '@/common/apis/bookings-query';
import { getRiskLevel } from '@/common/utilities/risk-levels';
import { useGetDocuments } from '@/common/apis/documents-query';
import useDocumentStore from '@/common/stores/document-store';
import { useRedesignView } from '@/common/apis/uat-redesign-view';
import { hasOptedOutOfUat } from '@/common/apis/uat';
import WhatsNewBanner from '@/common/components/banner/WhatsNewBanner';
import WhatsNewModal from '@/common/components/modal/WhatsNewModal';
import { RiskLevelType } from '@/common/constants/booking-types';
import ErrorPage from '@/common/components/errors/ErrorPage';
import { useGetPermissions } from '@/common/apis/permissions-query';
import { removeExtraSpace } from '@/common/utilities/display-utilities';
import usePrintStore from '@/common/stores/print-store';
import GuidedTour from '@/common/components/tour/GuidedTour';
import { getStepsForGuidedTour } from '@/common/utilities/home';

import { DetailTabs } from './DetailTabs';
import { ContactUs } from './contact-us/contactUs';
import EtaTracking from './eta/EtaTracking';
import BookingSummary from './booking-summary/BookingSummary';
import { ShareTrackingModal } from './share-tracking/ShareTrackingModal';
import { QuickReference } from './quick-reference/QuickReference';
import { ContainerSummary } from './container-summary/ContainerSummary';
import { ActionButtonGroup } from './action-button-group/ActionButtonGroup';
import { ExtraIdDetails } from './extraids/ExtraIdDetails';
import { launchdarkly } from '@/common/apis/launchdarkly';

interface HomeProps {
  bookingNumber: string;
}

export const Home = () => {
  const { bookingNumber: bookingNumberProp } = useParams<HomeProps>();
  const bookingNumber = parseInt(bookingNumberProp, 10);
  const { setPrintMode } = usePrintStore();

  // UAT
  const appconfig = useContext(ConfigContext) as AppConfig;
  const { isRedesignEnabled } = useRedesignView();
  const redirectUser = !isRedesignEnabled || hasOptedOutOfUat();

  const translate = useTranslation();
  const [showShareTrackingModal, setShowShareTrackingModal] = useState(false);
  const [displayWhatsNewModal, setDisplayWhatsNewModal] = useState(false);
  const [extraIdsEnabled, setExtraIdsEnabled] = useState(false);

  const { data: booking, isError, error, isLoading } = useGetBooking(bookingNumber);
  const { data: documents } = useGetDocuments(bookingNumber);
  const { setDocuments } = useDocumentStore();
  const customerInfo = booking?.customerInfo?.onlineContact;
  const customerPartyCode = booking?.customerInfo?.customer?.partyCode ?? '';
  const trackingReferenceNumber = booking?.trackingNumber ?? '';

  const { data: permissions } = useGetPermissions([customerPartyCode]);

  document.title = `${translate('Booking Details {0}', [bookingNumber])} | C.H. Robinson`;

  useEffect(() => {
    documents && setDocuments(documents);
  }, [documents, setDocuments]);

  useEffect(() => {
    window.addEventListener('beforeprint', () => {
      setPrintMode(true);
    });

    window.addEventListener('afterprint', () => {
      setPrintMode(false);
    });
  }, [setPrintMode]);

    
  useEffect(() => {
    const setFeaturesAllowedFromFlags = async () => {
      const flags = await launchdarkly();
      setExtraIdsEnabled(flags['online-visibility-enable-extra-ids-user']);
    };
    setFeaturesAllowedFromFlags();
  }, []);


  if (isLoading) {
    return (
      <Stack alignItems="center" mt={3}>
        <CircularProgress size="lg" label="Loading" labelPosition="right" />
      </Stack>
    );
  }

  if (isError && error || !booking) {
    return (
      <ErrorPage error={error} />
    );
  }

  if (redirectUser) {
    window.location.replace(`${appconfig?.baseUrl}/orders/#/detail/${bookingNumber}`);
    return null;
  }

  const primaryNumbers = removeExtraSpace(booking.customerInfo?.referenceNumbers?.map(rn => rn.value).join(', '), ' ') ?? '-';
  const etaLocation = booking.services && booking.services[0]?.deliverTo?.location;

  const toggleWhatsNewModal = () => {
    setDisplayWhatsNewModal(!displayWhatsNewModal);
  };

  const { label: riskLevel, color: riskColor } = getRiskLevel(booking.shipment?.riskLevel ?? RiskLevelType.Unassigned);

  const isMobileScreen = window.innerWidth < 1240;
  const steps = getStepsForGuidedTour(translate, isMobileScreen, booking);

  return (
    <Container className="app" sx={{ paddingBottom: 6 }}>
      <WhatsNewModal closeWhatsNewModal={toggleWhatsNewModal} isModalOpen={displayWhatsNewModal} />
      <GuidedTour steps={steps} />
      <Stack
        direction={{ sm: 'row' }}
        spacing={{ xs: 4 }}
        paddingBottom={{ xs: 2 }}
        justifyContent="space-between"
        sx={{
          '#go-back-button': {
            paddingLeft: '0'
          }
        }}
      >
        <ActionButtonGroup
          booking={booking}
          permissions={permissions}
          handleCancellation={() => null}
        />
      </Stack>

      <Typography variant="display200" marginBottom={2}>{translate('Booking Details')}</Typography>

      <Stack spacing={3}>
        <Paper
          className="booking-details"
          elevation={0}
          variant="outlined"
          sx={{
            borderColor: 'grey.300',
            borderLeft: 4,
            borderLeftColor: riskColor,
            padding: 2
          }}
        >
          <Stack
            columnGap={2}
            rowGap={2}
            direction={{ sm: 'column', md: 'row' }}
            justifyContent={{ sm: 'space-between' }}
          >
            <Stack
              direction="row"
              minWidth={0}
              data-testid="booking-detail-header"
            >
              <Typography component="span" variant="h3">{translate('Primary Number')}</Typography>
              <Tooltip title={primaryNumbers}>
                <Typography
                  component="p"
                  variant="h3"
                  noWrap
                  sx={{ marginLeft: 1 }}
                  data-testid="primary-number-field-value"
                >
                  {primaryNumbers}
                </Typography>
              </Tooltip>
            </Stack>

            <EtaTracking
              riskLevel={riskLevel}
              displayEtaDateTime={booking.shipment?.displayEta}
              displayEtaSource={booking.shipment?.displayEtaSource}
              location={etaLocation}
            />
          </Stack>

          <Divider sx={{ marginY: 2, borderBottomWidth: 2 }} />

          <BookingSummary booking={booking} />
          {extraIdsEnabled && <ExtraIdDetails extraIds={booking.extraIds} dataSourceLabels={booking.dataSourceLabels} />}
        </Paper>

        <QuickReference booking={booking} />
        <ContainerSummary services={booking.services ?? []} />
        <DetailTabs booking={booking} />

        {customerInfo && (
          <ContactUs
            email={customerInfo.emailAddress}
            phone={customerInfo.phoneNumber}
            bookingNumber={booking.customerBookingNumber ?? 0}
          />
        )}
      </Stack>

      <ShareTrackingModal
        open={showShareTrackingModal}
        handleClose={() => setShowShareTrackingModal(false)}
        trackingNumber={trackingReferenceNumber}
      />
    </Container>
  );
};
